import type { components } from '@connected/api-client/api.generated';
import type { FormApi, ReactFormApi } from '@tanstack/react-form';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { validators } from '@connected/api-client/generated/validators.generated';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { SingleOptionField, SingleOptionSumUpItem } from '@connected/form-fields/components/option';
import { useTranslation } from 'react-i18next';

import graph from '@/assets/graph.jpeg';

import type { CompletionFormValues } from '../utils/completion-types';

import { useIntervalQuestionsPatch } from '../hooks/use-completion-mutations';
import { calculateFactor } from '../utils/completion-utils';

type IntervalQuestionsFieldsProps = {
    form: FormApi<CompletionFormValues, ZodValidator> & ReactFormApi<CompletionFormValues, ZodValidator>;
    projectVersionId: string;
    conclusionData: components['schemas']['EditElectricalProjectInspectionConclusionQueryResult'] | undefined;
};

/**
 * Interval Questions fields component for Scope8, Nen1010, Nen3140 inspection kinds.
 * Renders the interval questions form fields and factor calculation graph.
 */
export function IntervalQuestionsFields({
    form,
    projectVersionId,
    conclusionData,
}: IntervalQuestionsFieldsProps) {
    const { t } = useTranslation('re-inspections');

    // Hook for patching interval questions
    const { patchField: intervalQuestionsPatchField } = useIntervalQuestionsPatch({
        projectVersionId,
    });

    // Don't render if interval questions are not enabled
    if (!conclusionData?.Inspection?.IntervalQuestions?.Enabled) {
        return null;
    }

    return (
        <div className="flex flex-col gap-4">
            <form.Field
                name="intervalQuestions.determineInspectionIntervalAge"
                validators={{
                    onChange: validators.patchElectricalProjectInspectionConclusionCommand.patchElectricalProjectInspectionConclusionCommandIntervalQuestions.determineInspectionIntervalAge,
                    onChangeAsync: intervalQuestionsPatchField('intervalQuestions.determineInspectionIntervalAge'),
                    onChangeAsyncDebounceMs: intervalQuestionsPatchField.debounceTime,
                }}
            >
                {field => (
                    <Label
                        label={t('completion.form.installationAge.label')}
                        className="w-full font-bold"
                    >
                        <SingleOptionField
                            name={field.name}
                            handleChange={field.handleChange}
                            state={field.state}
                            options={
                                conclusionData?.Inspection?.IntervalQuestions?.DetermineInspectionIntervalAgeList?.map(
                                    item => ({
                                        label: item.Descriptions!,
                                        value: item.Kind!,
                                    }),
                                ) || []
                            }
                            render={SingleOptionSumUpItem}
                        />
                        <FieldInfo state={field.state} />
                    </Label>
                )}
            </form.Field>
            <form.Field
                name="intervalQuestions.determineInspectionIntervalQuality"
                validators={{
                    onChange:
                        validators.patchElectricalProjectInspectionConclusionCommand.patchElectricalProjectInspectionConclusionCommandIntervalQuestions.determineInspectionIntervalQuality,
                    onChangeAsync: intervalQuestionsPatchField('intervalQuestions.determineInspectionIntervalQuality'),
                    onChangeAsyncDebounceMs: intervalQuestionsPatchField.debounceTime,
                }}
            >
                {field => (
                    <Label
                        label={t('completion.form.installationQuality.label')}
                        className="w-full font-bold"
                    >
                        <SingleOptionField
                            name={field.name}
                            handleChange={field.handleChange}
                            state={field.state}
                            options={
                                conclusionData?.Inspection?.IntervalQuestions?.DetermineInspectionIntervalQualityList?.map(
                                    item => ({
                                        label: item.Descriptions!,
                                        value: item.Kind!,
                                    }),
                                ) || []
                            }
                            render={SingleOptionSumUpItem}
                        />
                        <FieldInfo state={field.state} />
                    </Label>
                )}
            </form.Field>
            <form.Field
                name="intervalQuestions.determineInspectionIntervalEnvironment"
                validators={{
                    onChange:
                        validators.patchElectricalProjectInspectionConclusionCommand.patchElectricalProjectInspectionConclusionCommandIntervalQuestions.determineInspectionIntervalEnvironment,
                    onChangeAsync: intervalQuestionsPatchField('intervalQuestions.determineInspectionIntervalEnvironment'),
                    onChangeAsyncDebounceMs: intervalQuestionsPatchField.debounceTime,
                }}
            >
                {field => (
                    <Label
                        label={t('completion.form.surroundings.label')}
                        className="w-full font-bold"
                    >
                        <SingleOptionField
                            name={field.name}
                            handleChange={field.handleChange}
                            state={field.state}
                            options={
                                conclusionData?.Inspection?.IntervalQuestions?.DetermineInspectionIntervalEnvironmentList?.map(
                                    item => ({
                                        label: item.Descriptions!,
                                        value: item.Kind!,
                                    }),
                                ) || []
                            }
                            render={SingleOptionSumUpItem}
                        />
                        <FieldInfo state={field.state} />
                    </Label>
                )}
            </form.Field>
            <form.Field
                name="intervalQuestions.determineInspectionIntervalUsers"
                validators={{
                    onChange: validators.patchElectricalProjectInspectionConclusionCommand.patchElectricalProjectInspectionConclusionCommandIntervalQuestions.determineInspectionIntervalUsers,
                    onChangeAsync: intervalQuestionsPatchField('intervalQuestions.determineInspectionIntervalUsers'),
                    onChangeAsyncDebounceMs: intervalQuestionsPatchField.debounceTime,
                }}
            >
                {field => (
                    <Label
                        label={t('completion.form.installationUsers.label')}
                        className="w-full font-bold"
                    >
                        <SingleOptionField
                            name={field.name}
                            handleChange={field.handleChange}
                            state={field.state}
                            options={
                                conclusionData?.Inspection?.IntervalQuestions?.DetermineInspectionIntervalUsersList?.map(
                                    item => ({
                                        label: item.Descriptions!,
                                        value: item.Kind!,
                                    }),
                                ) || []
                            }
                            render={SingleOptionSumUpItem}
                        />
                        <FieldInfo state={field.state} />
                    </Label>
                )}
            </form.Field>
            <form.Field
                name="intervalQuestions.determineInspectionIntervalSupervision"
                validators={{
                    onChange: validators.patchElectricalProjectInspectionConclusionCommand.patchElectricalProjectInspectionConclusionCommandIntervalQuestions.determineInspectionIntervalSupervision,
                    onChangeAsync: intervalQuestionsPatchField('intervalQuestions.determineInspectionIntervalSupervision'),
                    onChangeAsyncDebounceMs: intervalQuestionsPatchField.debounceTime,
                }}
            >
                {field => (
                    <Label
                        label={t('completion.form.supervisionOverInstallation.label')}
                        className="w-full font-bold"
                    >
                        <SingleOptionField
                            name={field.name}
                            handleChange={field.handleChange}
                            state={field.state}
                            options={conclusionData?.Inspection?.IntervalQuestions?.DetermineInspectionIntervalSupervisionList?.map(
                                item => ({
                                    label: item.Descriptions!,
                                    value: item.Kind!,
                                }),
                            ) || []}
                            render={SingleOptionSumUpItem}
                        />
                        <FieldInfo state={field.state} />
                    </Label>
                )}
            </form.Field>

            {/* Grafiek */}
            <div className="flex flex-col items-center justify-center gap-4">
                <b>
                    <form.Subscribe selector={({ values }) => calculateFactor({ values, intervalQuestions: conclusionData!.Inspection!.IntervalQuestions! })}>
                        {({ factor, interval }) => (
                            <div className="flex flex-col items-center justify-center gap-2">
                                <p className="font-bold">
                                    Factor:
                                    {' '}
                                    <span className="underline">
                                        {factor}
                                    </span>
                                </p>
                                <p className="font-bold">
                                    Inspectie interval:
                                    {' '}
                                    <span className="underline">
                                        {interval}
                                        {' '}
                                        Jaar
                                    </span>
                                </p>
                            </div>
                        )}
                    </form.Subscribe>
                </b>
                <img className="w-7/12" src={graph} alt="Grafiek" />
            </div>
        </div>
    );
}
